"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OpenDialogOptions<R> {
  onClose?: (result: R) => Promise<void>;
}

export interface AlertOptions extends OpenDialogOptions<void> {
  title?: ReactNode;
  okText?: ReactNode;
}

export interface ConfirmOptions extends OpenDialogOptions<boolean> {
  title?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  severity?: "error" | "info" | "success" | "warning";
}

export interface PromptOptions extends OpenDialogOptions<string | null> {
  title?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
}

export interface DialogProps<P = undefined, R = void> {
  payload: P;
  open: boolean;
  onClose: (result: R) => Promise<void>;
}

type DialogComponent<P, R> = ComponentType<DialogProps<P, R>>;

interface DialogsApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  open: <P, R>(
    Component: DialogComponent<P, R>,
    payload?: P,
    options?: OpenDialogOptions<R>,
  ) => Promise<R>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  close: <R>(dialog: Promise<R>, result: R) => Promise<R>;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const DialogsContext = createContext<DialogsApi | null>(null);

// ─── Built-in dialog components ──────────────────────────────────────────────

interface AlertDialogPayload extends AlertOptions {
  msg: ReactNode;
}

function AlertDialog({
  open,
  payload,
  onClose,
}: DialogProps<AlertDialogPayload>) {
  const [loading, setLoading] = useState(false);
  const handleOk = async () => {
    setLoading(true);
    try {
      await onClose();
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={() => onClose()}>
      <DialogTitle>{payload.title ?? "Alert"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{payload.msg}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading || !open} onClick={handleOk}>
          {payload.okText ?? "Ok"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ConfirmDialogPayload extends ConfirmOptions {
  msg: ReactNode;
}

function ConfirmDialog({
  open,
  payload,
  onClose,
}: DialogProps<ConfirmDialogPayload, boolean>) {
  const [loading, setLoading] = useState(false);
  const handle = (result: boolean) => async () => {
    setLoading(true);
    try {
      await onClose(result);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={() => onClose(false)}>
      <DialogTitle>{payload.title ?? "Confirm"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{payload.msg}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button autoFocus disabled={loading || !open} onClick={handle(false)}>
          {payload.cancelText ?? "Cancel"}
        </Button>
        <Button
          color={payload.severity}
          disabled={loading || !open}
          onClick={handle(true)}
        >
          {payload.okText ?? "Ok"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface PromptDialogPayload extends PromptOptions {
  msg: ReactNode;
}

function PromptDialog({
  open,
  payload,
  onClose,
}: DialogProps<PromptDialogPayload, string | null>) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    try {
      await onClose(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onClose(input);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={() => onClose(null)}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      PaperProps={{ component: "form", onSubmit: handleSubmit } as any}
    >
      <DialogTitle>{payload.title ?? "Prompt"}</DialogTitle>
      <DialogContent>
        <DialogContentText>{payload.msg}</DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          type="text"
          fullWidth
          variant="standard"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={loading || !open} onClick={handleCancel}>
          {payload.cancelText ?? "Cancel"}
        </Button>
        <Button disabled={loading || !open} type="submit">
          {payload.okText ?? "Ok"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Internal stack entry ─────────────────────────────────────────────────────

interface DialogEntry {
  key: string;
  open: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: DialogComponent<any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  promise: Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (result: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose: (result: any) => Promise<void>;
}

// ─── Dialogs Provider ────────────────────────────────────────────────────────

export function DialogsProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<DialogEntry[]>([]);
  const keyCounter = useRef(0);
  const metadata = useRef(new WeakMap<Promise<unknown>, DialogEntry>());

  const open = useCallback(
    <P, R>(
      Component: DialogComponent<P, R>,
      payload?: P,
      options: OpenDialogOptions<R> = {},
    ): Promise<R> => {
      const { onClose = async () => {} } = options;
      let resolve!: (result: R) => void;
      const promise = new Promise<R>((res) => {
        resolve = res;
      });
      const key = `dialog-${keyCounter.current++}`;
      const entry: DialogEntry = {
        key,
        open: true,
        Component,
        payload,
        promise,
        resolve,
        onClose,
      };
      metadata.current.set(promise, entry);
      setStack((prev) => [...prev, entry]);
      return promise;
    },
    [],
  );

  const closeUi = useCallback((promise: Promise<unknown>) => {
    setStack((prev) =>
      prev.map((e) => (e.promise === promise ? { ...e, open: false } : e)),
    );
    setTimeout(
      () => setStack((prev) => prev.filter((e) => e.promise !== promise)),
      1000,
    );
  }, []);

  const close = useCallback(
    async <R,>(promise: Promise<R>, result: R): Promise<R> => {
      const entry = metadata.current.get(promise);
      if (!entry) throw new Error("Dialog not found");
      try {
        await entry.onClose(result);
      } finally {
        entry.resolve(result);
        closeUi(promise);
      }
      return promise;
    },
    [closeUi],
  );

  const api = useMemo(() => ({ open, close }), [open, close]);

  return (
    <DialogsContext.Provider value={api}>
      {children}
      {stack.map(({ key, open: isOpen, Component, payload, promise }) => (
        <Component
          key={key}
          payload={payload}
          open={isOpen}
          onClose={async (result: unknown) => {
            await close(promise, result);
          }}
        />
      ))}
    </DialogsContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface DialogHook {
  alert: (msg: ReactNode, options?: AlertOptions) => Promise<void>;
  confirm: (msg: ReactNode, options?: ConfirmOptions) => Promise<boolean>;
  prompt: (msg: ReactNode, options?: PromptOptions) => Promise<string | null>;
  open: DialogsApi["open"];
  close: DialogsApi["close"];
}

export function useDialogs(): DialogHook {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error("useDialogs must be used within a DialogsProvider");

  const alert = useCallback(
    (msg: ReactNode, { onClose, ...opts }: AlertOptions = {}) =>
      ctx.open(AlertDialog, { ...opts, msg }, { onClose }),
    [ctx],
  );

  const confirm = useCallback(
    (msg: ReactNode, { onClose, ...opts }: ConfirmOptions = {}) =>
      ctx.open(ConfirmDialog, { ...opts, msg }, { onClose }),
    [ctx],
  );

  const prompt = useCallback(
    (msg: ReactNode, { onClose, ...opts }: PromptOptions = {}) =>
      ctx.open(PromptDialog, { ...opts, msg }, { onClose }),
    [ctx],
  );

  return useMemo(
    () => ({ alert, confirm, prompt, open: ctx.open, close: ctx.close }),
    [alert, confirm, prompt, ctx],
  );
}
